import { useCallback, useEffect, useState } from "react";
import { BellRing, X, Download, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  getCurrentSubscription,
  isPushSupported,
  subscribeToPush,
} from "@/lib/pushSubscription";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type Step = "push" | "install" | null;

interface PushNotificationBannerProps {
  scope?: "global" | "local";
}

const isTopLevelWindow = () => {
  try {
    return window.self === window.top;
  } catch {
    return false;
  }
};

const isStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches;

export default function PushNotificationBanner({
  scope = "local",
}: PushNotificationBannerProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>(null);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);

  const syncBannerState = useCallback(async () => {
    if (scope !== "global" || !isTopLevelWindow()) {
      setStep(null);
      return;
    }

    const pushDismissed = localStorage.getItem("push_install_dismissed") === "1";
    const installDismissed = localStorage.getItem("install-dismissed") === "1";
    const supported = isPushSupported();
    const permission =
      typeof Notification !== "undefined" ? Notification.permission : "default";

    if (permission === "denied") {
      setStep(null);
      return;
    }

    let subscription = null;
    if (supported) {
      subscription = await getCurrentSubscription();

      if (!subscription && permission === "granted" && user) {
        subscription = await subscribeToPush(user.id);
      }
    }

    const pushEnabled = permission === "granted" || !!subscription;

    if (pushEnabled || !supported) {
      setStep(!installDismissed && !isStandaloneMode() ? "install" : null);
      return;
    }

    setStep(pushDismissed ? null : "push");
  }, [scope, user]);

  useEffect(() => {
    if (scope !== "global" || !isTopLevelWindow()) return;

    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      void syncBannerState();
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [scope, syncBannerState]);

  useEffect(() => {
    if (scope !== "global" || !isTopLevelWindow()) return;

    const timer = window.setTimeout(() => {
      void syncBannerState();
    }, 3000);

    const handleVisibilitySync = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      void syncBannerState();
    };

    window.addEventListener("focus", handleVisibilitySync);
    document.addEventListener("visibilitychange", handleVisibilitySync);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", handleVisibilitySync);
      document.removeEventListener("visibilitychange", handleVisibilitySync);
    };
  }, [scope, syncBannerState]);

  const handleAllowPush = async () => {
    const permission = await Notification.requestPermission();
    localStorage.setItem("push_install_dismissed", "1");

    if (permission === "granted" && user) {
      await subscribeToPush(user.id);
    }

    await syncBannerState();
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem("install-dismissed", "1");
      }
      setDeferredPrompt(null);
    }

    await syncBannerState();
  };

  const handleDismiss = () => {
    if (step === "push") {
      localStorage.setItem("push_install_dismissed", "1");
    } else {
      localStorage.setItem("install-dismissed", "1");
    }
    void syncBannerState();
  };

  if (scope !== "global" || !step) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50 max-w-xs animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="bg-card border border-border rounded-xl shadow-2xl p-4 flex items-start gap-3">
        {step === "push" ? (
          <>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <BellRing className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Ativar notificações?</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Receba atualizações sobre clínicas e novidades diretamente no seu dispositivo.
              </p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" className="text-xs h-7 gap-1" onClick={handleAllowPush}>
                  <BellRing className="h-3 w-3" /> Permitir
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={handleDismiss}>
                  Agora não
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Download className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Instalar Busca Clínicas</p>
              {isIOS ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Toque em <Share className="inline h-3.5 w-3.5 -mt-0.5" /> e depois em{" "}
                  <strong>"Adicionar à Tela Inicial"</strong>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Adicione à tela inicial para acesso rápido como um aplicativo.
                </p>
              )}
              {!isIOS && deferredPrompt && (
                <Button size="sm" className="mt-2 text-xs h-7 gap-1" onClick={handleInstall}>
                  <Download className="h-3 w-3" /> Instalar
                </Button>
              )}
            </div>
          </>
        )}
        <button onClick={handleDismiss} aria-label="Fechar" className="text-muted-foreground hover:text-foreground flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

